import type { ActivitySearchParams } from '@/features/admin/activityLogs/types/activity';
import type { CompanySearchParams } from '@/features/admin/companies/types/company';
import type { UserSearchParams } from '@/features/admin/users/types/user';

export const queryKeys = {
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (params?: UserSearchParams) =>
      params
        ? ([...queryKeys.users.lists(), params] as const)
        : queryKeys.users.lists(),
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    reportPermissions: (params?: unknown) =>
      params
        ? ([...queryKeys.users.all, 'reportPermissions', params] as const)
        : ([...queryKeys.users.all, 'reportPermissions'] as const),
  },

  roles: {
    all: ['roles'] as const,
    lists: () => [...queryKeys.roles.all, 'list'] as const,
    list: (params?: Record<string, unknown>) =>
      params
        ? ([...queryKeys.roles.lists(), params] as const)
        : queryKeys.roles.lists(),
    details: () => [...queryKeys.roles.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.roles.details(), id] as const,
  },

  reports: {
    all: ['reports'] as const,
    lists: () => [...queryKeys.reports.all, 'list'] as const,
    list: (params?: Record<string, unknown>) =>
      params
        ? ([...queryKeys.reports.lists(), params] as const)
        : queryKeys.reports.lists(),
    details: () => [...queryKeys.reports.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.reports.details(), id] as const,
    permitted: (moduleId: string) =>
      [...queryKeys.reports.all, 'permitted', moduleId] as const,
  },

  companies: {
    all: ['companies'] as const,
    lists: () => [...queryKeys.companies.all, 'list'] as const,
    list: (params?: CompanySearchParams) =>
      params
        ? ([...queryKeys.companies.lists(), params] as const)
        : queryKeys.companies.lists(),
    details: () => [...queryKeys.companies.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.companies.details(), id] as const,
  },

  departments: {
    all: ['departments'] as const,
    lists: () => [...queryKeys.departments.all, 'list'] as const,
    list: (params?: Record<string, unknown>) =>
      params
        ? ([...queryKeys.departments.lists(), params] as const)
        : queryKeys.departments.lists(),
    details: () => [...queryKeys.departments.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.departments.details(), id] as const,
  },

  reportFilters: {
    all: ['reportFilters'] as const,
    lists: () => [...queryKeys.reportFilters.all, 'list'] as const,
    list: (params?: Record<string, unknown>) =>
      params
        ? ([...queryKeys.reportFilters.lists(), params] as const)
        : queryKeys.reportFilters.lists(),
    details: () => [...queryKeys.reportFilters.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.reportFilters.details(), id] as const,
    history: (id: string, params?: Record<string, unknown>) =>
      params
        ? ([...queryKeys.reportFilters.all, 'history', id, params] as const)
        : ([...queryKeys.reportFilters.all, 'history', id] as const),
  },

  theme: {
    all: ['theme'] as const,
    current: () => [...queryKeys.theme.all, 'current'] as const,
  },

  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
  },

  applications: {
    all: ['applications'] as const,
    permitted: () => [...queryKeys.applications.all, 'permitted'] as const,
  },

  activities: {
    all: ['activities'] as const,
    lists: () => [...queryKeys.activities.all, 'list'] as const,
    list: (params?: ActivitySearchParams) =>
      params
        ? ([...queryKeys.activities.lists(), params] as const)
        : queryKeys.activities.lists(),
  },

  chatbots: {
    all: ['chatbots'] as const,
    lists: () => [...queryKeys.chatbots.all, 'list'] as const,
    list: (params?: Record<string, unknown>) =>
      params
        ? ([...queryKeys.chatbots.lists(), params] as const)
        : queryKeys.chatbots.lists(),
    details: () => [...queryKeys.chatbots.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.chatbots.details(), id] as const,
    users: (id: string, params?: Record<string, unknown>) =>
      params
        ? ([...queryKeys.chatbots.all, 'users', id, params] as const)
        : ([...queryKeys.chatbots.all, 'users', id] as const),
    my: () => [...queryKeys.chatbots.all, 'my'] as const,
  },
} as const;
